/**
 * Что показать на экране, который работает ПО ХОЗЯЙСТВУ, пока хозяйства нет
 * (AK-897; AK-866 В-3: область — из переключателя).
 *
 * Своего выбора хозяйства на экранах больше нет — ни дерева, ни пилюль, ни
 * кнопки «Хозяйство: …». Поэтому и на месте прежнего контрола только
 * объяснение, где выбирать: в переключателе вверху меню. Хозяйство есть —
 * компонент не рисует ничего: имя хозяйства и так стоит в переключателе.
 */
import { MapPin, ShieldOff } from 'lucide-react';
import { ErrorState } from '@/components/ErrorState';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import type { PlanningFarmScope } from './usePlanningFarmScope';

/**
 * Канон подписи пустого состояния «нужно хозяйство» — одна строка на всё
 * приложение (AK-898): переключатель стоит вверху левого меню и на десктопе,
 * и в мобильной шторке, поэтому «вверху меню», а не «слева»/«справа».
 * Экраны берут текст отсюда, не переписывают своими словами.
 */
export const PICK_FARM_TEXT = 'Выберите хозяйство в переключателе вверху меню';
export const NO_FARM_ACCESS_TEXT = 'Нет доступа к хозяйству';

export function FarmScopeNotice({
  scope,
  purpose = 'План сезона ведётся по каждому хозяйству отдельно.',
}: {
  scope: Pick<PlanningFarmScope, 'status' | 'message' | 'retry'>;
  /** Зачем экрану хозяйство — вторая строка просьбы выбрать. */
  purpose?: string;
}) {
  switch (scope.status) {
    case 'ready':
      return null;
    case 'loading':
      return <Skeleton className="h-12 w-full" />;
    case 'error':
      return (
        <ErrorState
          variant="inline"
          message={scope.message ?? 'Не удалось определить хозяйство.'}
          onRetry={scope.retry}
        />
      );
    case 'no-access':
      return (
        <Alert variant="warning">
          <ShieldOff className="h-4 w-4" />
          <AlertTitle>{NO_FARM_ACCESS_TEXT}</AlertTitle>
          <AlertDescription>
            Ссылка ведёт в хозяйство, которое вам не открыто. Выберите доступное в
            переключателе вверху меню или попросите доступ у администратора.
          </AlertDescription>
        </Alert>
      );
    case 'no-farms':
      return (
        <Alert>
          <MapPin className="h-4 w-4" />
          <AlertDescription>
            Хозяйств не видно. Обратитесь к администратору для настройки доступа.
          </AlertDescription>
        </Alert>
      );
    case 'pick':
      return (
        <Alert variant="info">
          <MapPin className="h-4 w-4" />
          <AlertTitle>{PICK_FARM_TEXT}</AlertTitle>
          <AlertDescription className="text-muted-foreground">{purpose}</AlertDescription>
        </Alert>
      );
  }
}
